import { createReducer, on } from '@ngrx/store';
import { User } from '../../models/user.model';
import * as fromUserActions from '../actions/users.action';
import { Team } from 'src/app/models/team.models';

export interface UserState {
  data: User;
  loaded: boolean;
  loading: boolean;
  error: string;
  clients: User[];
  teams: Team[];
  teamSelected: Team;
}

export const initialState: UserState = {
  data: undefined as any,
  loaded: false,
  loading: false,
  error: '',
  clients: undefined as any,
  teams: undefined as any,
  teamSelected: undefined as any
};

export const userReducer = createReducer(
  initialState,
  on(fromUserActions.AddUserAction, (state) => ({ ...state, loading: true })),
  on(fromUserActions.AddUserSuccessAction, (state, { data }) => ({
    ...state,
    data,
    loaded: true,
    loading: false
  })),
  on(fromUserActions.AddUserFailAction, (state, { error }) => ({
    ...state,
    error: error.message
  })),
  on(fromUserActions.DeleteUserAction, (state) => ({
    ...state,
    loading: true
  })),
  on(fromUserActions.DeleteUserSuccessAction, (state) => ({
    ...state,
    data: undefined as any,
    clients: [],
    loaded: true,
    loading: false
  })),
  on(fromUserActions.DeleteUserFailAction, (state, { error }) => ({
    ...state,
    error: error.message
  })),
  on(fromUserActions.UpdateUserAction, (state) => ({
    ...state,
    loading: true
  })),
  on(fromUserActions.UpdateUserSuccessAction, (state, { data }) => ({
    ...state,
    data,
    loaded: true,
    loading: false
  })),
  on(fromUserActions.UpdateUserFailAction, (state, { error }) => ({
    ...state,
    error: error.message
  })),
  on(fromUserActions.FetchClientsByTrainerSuccessAction, (state, { data }) => ({
    ...state,
    clients: data,
    loading: false
  })),
  on(fromUserActions.FetchClientsByTrainerFailAction, (state, { error }) => ({
    ...state,
    error: error.message,
    loading: false
  })),
  on(fromUserActions.FetchTeamsByTrainerSuccessAction, (state, { data }) => ({
    ...state,
    teams: data,
    loading: false
  })),
  on(fromUserActions.FetchTeamsByTrainerFailAction, (state, { error }) => ({
    ...state,
    error: error.message,
    loading: false
  })),
  on(fromUserActions.SelectTeamAction, (state) => ({
    ...state,
    teamSelected: undefined as any,
    loading: true
  })),
  on(fromUserActions.SelectTeamSuccessAction, (state, { data }) => ({
    ...state,
    teamSelected: data,
    loading: false
  })),
  on(fromUserActions.SelectTeamFailAction, (state, { error }) => ({
    ...state,
    error: error.message,
    loading: false
  })),
  on(fromUserActions.ResetSelectedTeamAction, (state) => ({
    ...state,
    teamSelected: undefined as any,
    loading: false
  })),
  on(fromUserActions.AddTeamAction, (state) => ({
    ...state,
    loading: true
  })),
  on(fromUserActions.AddTeamSuccessAction, (state, { data }) => ({
    ...state,
    teams: [...state.teams, ...[data]],
    loading: false
  })),
  on(fromUserActions.AddTeamFailAction, (state, { error }) => ({
    ...state,
    error: error.message,
    loading: false
  })),
  on(fromUserActions.UpdateTeamAction, (state) => ({
    ...state,
    loading: true
  })),
  on(fromUserActions.UpdateTeamSuccessAction, (state, { data }) => ({
    ...state,
    teams: state.teams.map((team) => (data.id === team.id ? data : team)),
    loading: false
  })),
  on(fromUserActions.UpdateTeamFailAction, (state, { error }) => ({
    ...state,
    error: error.message,
    loading: false
  })),
  on(fromUserActions.DeleteTeamAction, (state) => ({
    ...state,
    loading: true
  })),
  on(fromUserActions.DeleteTeamSuccessAction, (state, { id }) => ({
    ...state,
    teams: state.teams.filter((team) => team.id !== id),
    loading: false
  })),
  on(fromUserActions.DeleteTeamFailAction, (state, { error }) => ({
    ...state,
    error: error.message,
    loading: false
  })),
  on(fromUserActions.ChangeTeamAction, (state) => ({
    ...state,
    loading: true
  })),
  on(
    fromUserActions.ChangeTeamSuccessAction,
    (state, { userId, oldTeam, newTeam }) => ({
      ...state,
      teams: upsertTeams(
        state.clients.find((client) => client.id === userId)!,
        oldTeam,
        state.teams,
        newTeam
      ),
      teamSelected: upsertActualTeam(
        state.clients.find((client) => client.id === userId)!,
        oldTeam
      ),
      loading: false
    })
  ),
  on(fromUserActions.ChangeTeamFailAction, (state, { error }) => ({
    ...state,
    error: error.message,
    loading: false
  })),
  on(fromUserActions.UnsubscribeUserAction, (state) => ({
    ...state,
    loading: true
  })),
  on(
    fromUserActions.UnsubscribeUserSuccessAction,
    (state, { userId, oldTeam }) => ({
      ...state,
      teams: upsertTeams(
        state.clients.find((client) => client.id === userId)!,
        oldTeam,
        state.teams
      ),
      teamSelected: upsertActualTeam(
        state.clients.find((client) => client.id === userId)!,
        oldTeam
      ),
      clients: state.clients.filter((client) => client.id !== userId),
      loading: false
    })
  ),
  on(fromUserActions.UnsubscribeUserFailAction, (state, { error }) => ({
    ...state,
    error: error.message,
    loading: false
  }))
);

function upsertTeams(
  user: User,
  oldTeam: Team,
  actualTeams: Team[],
  newTeam?: Team
): Team[] {
  return actualTeams.map((team) => {
    let teamToReturn = { ...team };
    if (team.id === newTeam?.id) {
      teamToReturn.members = [...teamToReturn.members, user];
    } else if (team.id === oldTeam.id) {
      teamToReturn.members = teamToReturn.members.filter(
        (member) => member.id !== user.id
      );
    }
    return teamToReturn;
  });
}

function upsertActualTeam(user: User, team: Team): Team {
  return {
    ...team,
    members: team.members.filter((member) => member.id !== user.id)
  };
}
